import { Recipe } from '../../types/recipe';
import { generateRecipeCleanup, generateImagePrompt } from '../ai/aiGateway';
import { fenceUntrusted } from '../assistant/security';

export interface GeminiResponse {
  success: boolean;
  text?: string;
  error?: string;
  debugInfo?: any;
  metadata?: {
    type?: string;
    recipeId?: string;
    prompt?: string;
    [key: string]: any;
  };
}

export async function extractKeyVisualElements(recipe: Recipe): Promise<string> {

  try {
    const recipeContext = fenceUntrusted(
      `Title: ${recipe.title}
      Description: ${recipe.description || 'No description provided.'}
      Ingredients: ${JSON.stringify(recipe.ingredients)}
      Instructions: ${recipe.method || 'No instructions provided.'}`,
      'recipe context',
    );

    const prompt = `Analyze the following recipe and identify the 3-5 most important and visually appealing elements for an image. Focus on ingredients with strong colors, textures, and the final presentation of the dish. List them as a simple, comma-separated string.

      For example, for a grilled cheese with coriander sauce, the output should be:
      "Melted Fontinella cheese oozing out, vibrant green coriander sauce, creamy avocado slices, crispy bacon, perfectly toasted bread"

      Do not add any preamble. Return ONLY the comma-separated list.

      Recipe:
      ${recipeContext}
    `;

    // Route through the AI gateway so this respects the admin-configured text
    // provider (Gemini OR Venice) instead of being pinned to Gemini.
    const result = await generateImagePrompt(prompt, 'extractKeyVisualElements');

    if (result.success && result.text) {
      const extracted = result.text.trim();
      return extracted;
    } else {
      return `a delicious plate of ${recipe.title}`;
    }
  } catch {
    return `a delicious plate of ${recipe.title}`;
  }
}

export async function polishPrompt(assembledPrompt: string): Promise<string> {
  const metaPrompt = `You are a creative director. Rewrite the following machine-generated image prompt to sound more natural, fluent, and evocative.
  
  **CRITICAL RULE: You must retain all specific food elements and ingredients mentioned.** Do not add or remove key ingredients.
  
  Return ONLY the polished prompt.

  Machine Prompt: "${fenceUntrusted(assembledPrompt, 'machine-generated image prompt')}"

  Polished Prompt:`;

  // Route through the AI gateway (provider-agnostic: Gemini or Venice).
  const result = await generateImagePrompt(metaPrompt, 'polishPrompt');

  if (result.success && result.text) {
    return result.text.trim();
  } else {
    return assembledPrompt;
  }
}

export async function cleanupRecipeMarkdown(recipeText: string): Promise<GeminiResponse> {
  const systemPrompt = `You are an expert recipe editor. Your task is to reformat the user-provided recipe markdown to fit a clean, standardized structure.

Rules:
1.  DO NOT change the meaning, quantities, ingredients, or steps of the recipe.
2.  DO NOT add any ingredients or steps that are not in the original text.
3.  Standardize the structure into the following sections using markdown:
    *   A single # Title.
    *   A brief description (1 sentence).
    *   A ## Ingredients section with each ingredient on a new line, formatted as an unordered list (- ).
    *   A ## Method section with each step on a new line, formatted as an ordered list (1. ).
4.  Ensure consistent spacing and markdown syntax.
5.  If the original recipe is missing a section, omit that section from the output.

Recipe to Clean:
'''markdown
${fenceUntrusted(recipeText, 'recipe markdown')}
'''`;

  const result = await generateRecipeCleanup(systemPrompt, 'cleanupRecipeMarkdown');

  if (result.success && result.text) {
    return { success: true, text: result.text.trim() };
  } else {
    return {
      success: false,
      error: result.error || "Failed to clean up recipe markdown due to an unknown issue.",
      debugInfo: result.debugInfo
    };
  }
}

export async function createRecipeFromRawText(rawText: string): Promise<GeminiResponse> {
  const systemPrompt = `You are a recipe expert. Your task is to convert messy recipe text into a clean, organized format, standardizing the formatting while preserving the original content and structure.

ALWAYS output in this EXACT format with ALL sections:

# [Descriptive Recipe Title]

## Description
[One or two sentence description of the dish. This section is mandatory.]

## Ingredients
[List all ingredients here, formatted as an unordered list (e.g., "- 1 cup flour"). If the original text has ingredients listed before the first subgroup, list them first here without a heading.]

[If ingredients are grouped in the source, preserve the groupings and their original order like this:]
**For the Tomato Puree:**
- [ingredient 1 with amount]
- [ingredient 2 with amount]

## Method
[List all instructions here, formatted as an ordered list (e.g., "1. Preheat oven..."). If instructions have sections, preserve them and their original order.]

## Notes
[Combine ALL supplementary text here from sections like "Recipe Notes", "Notes", "Tips", etc. Preserve all this information without summarizing.]

CRITICAL RULES:
1. **FORMATTING:** Standardize all ingredient lists to use unordered markdown bullets ('-') and all instruction lists to use ordered markdown numbers ('1.').
2. **ORDERING:** DO NOT reorder ingredients or ingredient groups.
3. **CATEGORIES:** DO NOT invent new ingredient categories.
4. **GROUPING:** Preserve all original groupings and their exact titles.
5. **NOTES:** The '## Notes' section should ONLY contain content from a 'Recipe Notes', 'Notes', or similarly-headed section.
6. **COMPLETENESS:** Extract ALL content.
7. **TITLE:** Create a descriptive title.

Here's the messy recipe text to organize:

${fenceUntrusted(rawText, 'messy recipe text')}

Convert to organized format:`;

  const result = await generateRecipeCleanup(systemPrompt, 'createRecipeFromRawText');

  if (result.success && result.text) {
    return { success: true, text: result.text.trim() };
  } else {
    return {
      success: false,
      error: result.error || "Failed to structure recipe from raw text due to an unknown issue.",
      debugInfo: result.debugInfo
    };
  }
}
